import { useEffect, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { Check, Clock, Hourglass, Lightbulb, Lock, ScrollText } from 'lucide-react';
import { PlayerChip, usePartyUi } from '@gmp/shared-ui';
import type { RevealEntry } from '../turns';

/** "38s", "4m 10s", "3h 12m", "2d 5h": readable at every pace. */
export function formatLeft(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** Remaining time, ticking once a second (enough for any pace, cheap on phones). */
export function useLeft(endsAt: number | null): number | null {
  const { now } = usePartyUi();
  const [left, setLeft] = useState<number | null>(endsAt === null ? null : endsAt - now());
  useEffect(() => {
    if (endsAt === null) return setLeft(null);
    const tick = () => setLeft(Math.max(0, endsAt - now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [endsAt, now]);
  return left;
}

/**
 * Top of every async game screen: game name, the step everyone is on, the round, and the real deadline
 * (with the local clock time for long phases, so "ends Tue 9:15 PM" is clear across time zones).
 */
export function AsyncHeader({ title, step, round, totalRounds, endsAt, accent }: { title: string; step: string; round?: number; totalRounds?: number; endsAt: number | null; accent?: string }) {
  const left = useLeft(endsAt);
  const long = left !== null && left > 3600_000;
  return (
    <header className="flex items-center gap-3 px-4 pt-3 pb-2" data-testid="async-header">
      <div className="min-w-0 flex-1">
        <p className="font-display font-black text-lg leading-tight truncate" style={{ color: accent ?? 'var(--accent)' }}>{title}</p>
        <p className="text-sm text-mute font-bold truncate">
          {round && totalRounds ? `Round ${round} of ${totalRounds} · ` : ''}{step}
        </p>
      </div>
      {left !== null && (
        <div className="chip shrink-0 py-1.5" role="timer" aria-label={`Time left: ${formatLeft(left)}`} data-testid="async-left">
          <Clock size={14} aria-hidden />
          <span className="font-bold tabular-nums">{formatLeft(left)}</span>
          {long && endsAt !== null && <span className="text-mute text-xs hidden sm:inline">· {new Date(endsAt).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</span>}
        </div>
      )}
    </header>
  );
}

/** The card holding the player's decision for this step. */
export function YourMove({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="card p-4 flex flex-col gap-3" aria-label={title} data-testid="your-move">
      <div>
        <p className="chip uppercase tracking-widest text-[11px] w-fit mb-1">Your move</p>
        <h2 className="font-display font-black text-2xl leading-tight">{title}</h2>
        {hint && <p className="text-sm text-mute mt-1">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/** Shown after submitting: what is locked in, and that they can leave and come back. */
export function LockedIn({ summary, canChange, onChange }: { summary: ReactNode; canChange?: boolean; onChange?: () => void }) {
  return (
    <motion.section initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="card p-4 flex flex-col gap-2 border-good" data-testid="locked-in">
      <p className="flex items-center gap-2 font-display font-black text-xl text-good"><Lock size={20} aria-hidden /> Locked in</p>
      <div className="text-paper/90">{summary}</div>
      <p className="text-sm text-mute">You can close the app. Results appear here when everyone has played or the time runs out.</p>
      {canChange && onChange && <button className="btn btn-sm btn-secondary w-fit" onClick={onChange}>Change my answer</button>}
    </motion.section>
  );
}

/** Who the step is still waiting on (names only, never what anyone chose). */
export function WaitingOn({ ids, away = [], label = 'Waiting on' }: { ids: string[]; away?: string[]; label?: string }) {
  const { member } = usePartyUi();
  if (ids.length === 0 && away.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-label={label} data-testid="waiting-on">
      {ids.length > 0 && (
        <>
          <p className="text-sm text-mute font-bold flex items-center gap-1.5"><Hourglass size={14} aria-hidden /> {label} {ids.length}</p>
          <div className="flex flex-wrap gap-2">{ids.map((id) => <span key={id} className="chip"><PlayerChip member={member(id)} size={22} /></span>)}</div>
        </>
      )}
      {away.length > 0 && <p className="text-xs text-mute">Away (the game carries on without waiting): {away.map((id) => member(id)?.nickname ?? 'Former player').join(', ')}</p>}
    </section>
  );
}

/** Everyone who has acted (ticks only, still no choices). */
export function DoneList({ ids }: { ids: string[] }) {
  const { member } = usePartyUi();
  if (!ids.length) return null;
  return (
    <div className="flex flex-wrap gap-2" aria-label="Already played">
      {ids.map((id) => (
        <span key={id} className="chip opacity-80"><Check size={12} className="text-good" aria-hidden /> {member(id)?.nickname ?? 'Former player'}</span>
      ))}
    </div>
  );
}

/** Every earlier reveal, newest first, so someone back after a day can catch up. */
export function RevealHistory({ entries }: { entries: RevealEntry[] }) {
  const [open, setOpen] = useState(false);
  if (!entries.length) return null;
  const list = [...entries].reverse();
  return (
    <section className="card p-4" data-testid="reveal-history">
      <button className="flex items-center gap-2 font-display font-black text-lg w-full text-left" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <ScrollText size={18} aria-hidden /> What happened so far <span className="text-mute text-sm font-sans ml-auto">{open ? 'Hide' : `${entries.length}`}</span>
      </button>
      {open && (
        <ol className="mt-3 flex flex-col gap-3">
          {list.map((e, i) => (
            <li key={i} className="border-l-2 border-[var(--accent)] pl-3">
              <p className="font-bold">Round {e.round}: {e.title}</p>
              {e.lines.map((l, j) => <p key={j} className="text-sm text-paper/80">{l}</p>)}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export interface TutorialExample {
  /** The situation shown in the example round. */
  prompt: string;
  /** Pick one: each choice explains what would happen and what it would score. */
  choices: { label: string; outcome: string }[];
}

/**
 * Short tutorial with a playable example: the steps, then a pretend round where tapping a choice shows
 * exactly how it would play out. It never touches the real game.
 */
export function Tutorial({ steps, example, onDone }: { steps: { title: string; body: string }[]; example: TutorialExample; onDone?: () => void }) {
  const [picked, setPicked] = useState<number | null>(null);
  return (
    <section className="card p-4 flex flex-col gap-4" aria-label="How to play" data-testid="tutorial">
      <h2 className="font-display font-black text-2xl flex items-center gap-2"><Lightbulb size={22} aria-hidden /> How it works</h2>
      <ol className="flex flex-col gap-2">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span className="grid place-items-center size-7 rounded-full bg-[var(--accent)] text-ink font-black shrink-0">{i + 1}</span>
            <span><b>{s.title}.</b> <span className="text-paper/85">{s.body}</span></span>
          </li>
        ))}
      </ol>
      <div className="rounded-2xl bg-ink-3/60 p-3 flex flex-col gap-2">
        <p className="text-xs uppercase tracking-widest text-mute font-bold">Try it</p>
        <p className="font-bold">{example.prompt}</p>
        <div className="grid gap-2">
          {example.choices.map((c, i) => (
            <button key={i} className={`btn btn-sm ${picked === i ? '' : 'btn-secondary'} justify-start text-left`} onClick={() => setPicked(i)} aria-pressed={picked === i} data-testid={`tutorial-choice-${i}`}>
              {c.label}
            </button>
          ))}
        </div>
        {picked !== null && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-paper/90" data-testid="tutorial-outcome">{example.choices[picked]!.outcome}</motion.p>}
      </div>
      {onDone && <button className="btn" onClick={onDone}>Got it</button>}
    </section>
  );
}

/** Wrapper for an async game screen: header, a readable column, safe area at the bottom. */
export function AsyncScreen({ header, children }: { header: ReactNode; children: ReactNode }) {
  return (
    <div className="flex-1 flex flex-col">
      {header}
      <main className="flex-1 flex flex-col gap-3 px-4 pb-6 safe-bottom max-w-2xl w-full mx-auto">{children}</main>
    </div>
  );
}
